import { LazyLoadImage } from "react-lazy-load-image-component";
import { SolBadge, SolButton, SolIcon } from "@solstice/sol-react";
import LinkedinIcon from "assets/icons/linkedin.svg";
import { BrokerProps, LicenseProps, ProfileProps } from "types";
import { LoadingPage } from "ui-molecules";
import { Collapsable } from "ui-atoms";
import { capitalize, joinArray, parseOriginalJSON } from "utils";
import { useEffect, useRef, useState } from "react";
import cn from "classnames";
import { Tooltip } from "react-tooltip";
import { renderToString } from "react-dom/server";
import { applyCloudinaryTransformations } from "utils/cloudinaryTransformations";
import { LicensePreview } from "../License";

interface PreviewProps {
  profile?: ProfileProps | null;
  broker?: BrokerProps | null;
  isLoading?: boolean;
}

const TAGS: { label: string; key: keyof ProfileProps }[] = [
  { label: "Services", key: "services" },
  { label: "Industry", key: "industries" },
  { label: "Property Type", key: "property_types" },
];

const Preview: React.FC<PreviewProps> = ({ profile, broker, isLoading }) => {
  const [bgHeight, setBgHeight] = useState(320);
  const ref = useRef<any>(null);
  const isEmptyExpertise =
    !profile?.["services"]?.length &&
    !profile?.["industries"]?.length &&
    !profile?.["property_types"]?.length;

  useEffect(() => {
    if (!ref?.current) return;
    setTimeout(() => {
      setBgHeight(
        ref?.current?.clientHeight ? ref?.current?.clientHeight + 48 : 320
      );
    }, 100);
  }, [ref?.current]);

  return (
    <>
      {isLoading ? (
        <LoadingPage rows={12} />
      ) : (
        <>
          <div className="flex flex-col w-full px-10 pt-12 relative">
            <div
              className="bg-jll-surface-base-secondary-subdued absolute top-0 left-0 right-0 z-0"
              style={{ height: `${bgHeight}px` }}
            />
            <div className="grid grid-cols-12 mb-9 z-0">
              <LazyLoadImage
                src={applyCloudinaryTransformations({
                  src: broker?.photos?.[0] as string,
                  transformations: { width: 500 },
                })}
                className="rounded-md col-span-3"
              />
              <div />
              <div className="col-span-8">
                <div className="pb-[56px]" ref={ref}>
                  <div className="mb-6 flex flex-row justify-between items-start">
                    <div>
                      <h3 className="text-[32px] mb-6">
                        {[broker?.first_name, broker?.last_name].join(" ")}
                      </h3>
                      <p className="text-xl text-jll-text-base-subdued">
                        {profile?.job_title || ""}
                      </p>
                    </div>

                    <Tooltip id={`preview_${profile?.id}_compliant`} />
                  </div>

                  {!isEmptyExpertise && (
                    <div className="flex flex-col space-y-4">
                      <p className="">Expertise</p>
                      <div className="flex flex-row items-center flex-wrap">
                        {TAGS?.map(({ label, key }, idx) => {
                          if (!(profile as ProfileProps)?.[key]?.length) return;
                          return (
                            <>
                              {profile?.[key]?.map(
                                (item: any, idx1: number) => (
                                  <SolBadge
                                    key={idx1}
                                    size="small"
                                    variant="filled"
                                    className="mb-2 mr-2"
                                  >
                                    <span>
                                      {capitalize(item?.name || item)}
                                    </span>
                                  </SolBadge>
                                )
                              )}
                            </>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex flex-col space-y-6 mt-10 mb-4">
                  <p className="text-xl">Get in touch</p>
                  <p>{`${
                    profile?.cities?.length
                      ? `${joinArray(
                          [
                            profile?.cities?.[0]?.name,
                            profile?.states?.[0]?.name,
                          ],
                          ", "
                        )}  |  `
                      : ""
                  }${profile?.country?.name || ""}`}</p>
                  {broker?.telephone && <p>{broker?.telephone || ""}</p>}
                  {broker?.email && (
                    <a
                      href={`mailto:${broker?.email}`}
                      target="_blank"
                      className="underline"
                    >
                      {broker?.email || ""}
                    </a>
                  )}
                  {profile?.license?.length && (
                    <p>License # {profile?.license ?? ""}</p>
                  )}
                  {!!broker?.linkedin?.length && (
                    <div>
                      <SolButton
                        variant="secondary"
                        onSol-click={() => {
                          window.open(broker?.linkedin, "_blank");
                        }}
                      >
                        <div className="flex flex-row items-center">
                          <img src={LinkedinIcon} className="mr-2" />
                          Linkedin
                        </div>
                      </SolButton>
                    </div>
                  )}
                </div>

                <LicensePreview licenses={broker?.licenses} />

                <div className="">
                  {profile?.bio && (
                    <div
                      className="bio-preview mb-12"
                      dangerouslySetInnerHTML={{
                        __html: parseOriginalJSON(profile?.bio),
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Preview;
